import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { Icon, Tooltip } from "@bedrock/ui";

export interface PaletteColorDetails {
  css: string;
  hex: string;
  rgb: string;
}

function clampChannel(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

function detailsFromChannels(css: string, channels: number[]): PaletteColorDetails {
  const [red = 0, green = 0, blue = 0] = channels.map(clampChannel);
  const hex = `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
  return { css, hex, rgb: `rgb(${red}, ${green}, ${blue})` };
}

export function paletteColorDetails(color: string): PaletteColorDetails {
  const value = color.trim();
  const hex = value.match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1];
  if (hex) {
    const expanded = hex.length === 3 ? [...hex].map((character) => `${character}${character}`).join("") : hex;
    return detailsFromChannels(value, [0, 2, 4].map((offset) => Number.parseInt(expanded.slice(offset, offset + 2), 16)));
  }

  const rgb = value.match(/^rgba?\((.+)\)$/i)?.[1]
    ?.split(/[\s,/]+/)
    .filter(Boolean)
    .slice(0, 3)
    .map(Number);
  if (rgb?.length === 3 && rgb.every(Number.isFinite)) return detailsFromChannels(value, rgb);

  return detailsFromChannels(value, [0, 0, 0]);
}

function PaletteSwatch({ color }: { color: string }) {
  const details = paletteColorDetails(color);
  const detailsId = useId();
  const clearFeedbackTimer = useRef<number | undefined>(undefined);
  const [copied, setCopied] = useState<"rgb" | "hex" | null>(null);

  useEffect(() => () => window.clearTimeout(clearFeedbackTimer.current), []);

  const copy = async (format: "rgb" | "hex", value: string) => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(format);
      window.clearTimeout(clearFeedbackTimer.current);
      clearFeedbackTimer.current = window.setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied(null);
    }
  };

  return <li className="media-palette__item" style={{ "--media-color": details.css } as CSSProperties}>
    <Tooltip label={`Copy ${details.hex}`}><button
      type="button"
      className="media-palette__swatch"
      aria-label={`Copy color ${details.hex}`}
      aria-describedby={detailsId}
      onClick={() => void copy("hex", details.hex)}
    /></Tooltip>
    <div className="media-palette__details" id={detailsId}>
      <header>
        <span>Color values</span>
        <small aria-live="polite">{copied ? `${copied.toUpperCase()} copied` : "Select to copy"}</small>
      </header>
      <button type="button" aria-label={`Copy RGB value ${details.rgb}`} onClick={() => void copy("rgb", details.rgb)}>
        <span>RGB</span><code>{details.rgb}</code><Icon name="copy"/>
      </button>
      <button type="button" aria-label={`Copy HEX value ${details.hex}`} onClick={() => void copy("hex", details.hex)}>
        <span>HEX</span><code>{details.hex}</code><Icon name="copy"/>
      </button>
    </div>
  </li>;
}

export function MediaPalette({ colors }: { colors: string[] }) {
  return <aside className="media-palette" aria-label="Image color palette">
    <ol className="media-palette__list">
      {colors.map((color, index) => <PaletteSwatch key={`${color}-${index}`} color={color}/>)}
    </ol>
  </aside>;
}
